import { BindingEngine, computedFrom, Disposable } from "aurelia-binding";
import { autoinject, transient } from "aurelia-dependency-injection";
import { FileHelper } from "../../../../../../../helpers/file-helper";
import { NotificationClientService, NotificationType } from "../../../../../../../services/basics/notification-client.service";
import { PortsAndTerminalsService } from "../../../services/ports-and-terminals.service";
import * as Enumerable from "linq";
import { AddVesselToScheduleDialogModel, IPartialAddVesselSchedule } from "../add-vessel-to-schedule-dialog";
import { BrowserHelper } from "../../../../../../../helpers/browser-helper";
import { PortsTerminalBerthsResponseItem, PortTerminalAndBerthResponse } from "../../../models/ports-and-terminals.models";
import { useView } from "aurelia-framework";

@autoinject
@transient()
@useView("views/pages/modules/ports-and-terminals/dialogs/add-vessel-to-schedule-dialog/partial-upload-vessel-list/partial-upload-vessel-list.html")
export class PartialUploadVesselList implements IPartialAddVesselSchedule {

    private _portTerminalAndBerthResponse: PortTerminalAndBerthResponse;
    private readonly _supportedTypes = [
        {
            type: "xlsx",
            mime: ""
        },
        {
            type: "xlsx",
            mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        },
        {
            type: "xlsm",
            mime: "application/vnd.ms-excel.sheet.macroEnabled.12"
        },
        {
            type: "xlsb",
            mime: "application/vnd.ms-excel.sheet.binary.macroEnabled.12"
        },
        {
            type: "ods",
            mime: "application/vnd.oasis.opendocument.spreadsheet"
        },
        {
            type: "csv",
            mime: "text/csv"
        },
        {
            type: "csv",
            mime: "application/vnd.ms-excel",
            extension: "csv" //added because this mime type is also for xls
        }
    ];
    private _subscriptions: Disposable[] = [];

    public countryId: number;
    public portId: number;
    public properties: EntityProperty[] = [
        {
            property: "Vessel Name",
            possibleNameMatches: ["Vessel Name", "Name", "VesselName", "Vessel"],
            isRequired: false
        },
        {
            property: "Vessel IMO",
            possibleNameMatches: ["Vessel IMO", "IMO", "InternationalMaritimeOrganizationNumber", "InternationalMaritimeOrganization"],
            isRequired: true
        },
        {
            property: "Terminal Name",
            possibleNameMatches: ["Terminal Name", "Terminal", "TerminalName"],
            isRequired: false
        },
        {
            property: "Berth Name",
            possibleNameMatches: ["Berth Name", "Berth", "BerthName"],
            isRequired: false
        },
        {
            property: "Arrival Date",
            possibleNameMatches: ["Arrival Date", "eta", "ArrivalDate", "Arrival Date (dd/mm/yyyy)", "Arrival Date (yyyy-mm-dd)"],
            isRequired: true
        },
        {
            property: "Arrival Time",
            possibleNameMatches: ["Arrival Time", "ArrivalTime", "Arrival Time (24 hours hh:mm)"],
            isRequired: true
        }
    ];
    public isMappingConfig: boolean;
    public vesselPortCallsFiles: File[] = [];
    public mappings: PropertyMappingModel[] = [];
    public onSave: () => void;
    public isSafari: boolean = BrowserHelper.isSafari();
    public countries: PortsTerminalBerthsResponseItem[] = [];

    @computedFrom("countryId")
    public get ports(): PortsTerminalBerthsResponseItem[] {
        return this.countryId ? this._portTerminalAndBerthResponse.ports.filter(x => x.parentId == this.countryId) : [];
    }

    @computedFrom("isMappingConfig", "vesselPortCallsFiles", "vesselPortCallsFiles.length")
    public get canSubmit(): boolean {
        return this.isMappingConfig && this.vesselPortCallsFiles.length > 0;
    }

    @computedFrom("_supportedTypes")
    public get acceptTypes(): string {
        return this._supportedTypes.filter(x => x.mime && x.mime.trim() != "").map(x => x.mime).join(",");
    }

    constructor(
        private portsAndTerminalsService: PortsAndTerminalsService,
        private notificationClientService: NotificationClientService,
        private bindingEngine: BindingEngine) {
    }

    activate(model: AddVesselToScheduleDialogModel) {
        this.onSave = model.onSave;
        const subscription = this.bindingEngine.collectionObserver(this.vesselPortCallsFiles).subscribe(() => {
            this.vesselPortCallsFilesChanged();
        });

        this._subscriptions.push(subscription);

        this.portsAndTerminalsService.loadPortsTerminalsAndBerths().then(response => {
            this.countries = response.countries;
            this._portTerminalAndBerthResponse = response;
        });
    }

    submit() {
        return new Promise<void>((resolve, reject) => {
            if (!this.vesselPortCallsFiles || this.vesselPortCallsFiles.length <= 0) {
                this.notificationClientService.show("No File uploaded", "", NotificationType.Error);
                resolve();
                return;
            }

            if (!this.portId) {
                this.notificationClientService.show("No Port selected", "", NotificationType.Error);
                resolve();
                return;
            }

            const validationResult = this.validateFiles();
            if (!validationResult.valid) {
                this.notificationClientService.show(`Error`, validationResult.validationMessage, NotificationType.Error);
                reject();
                return;
            }

            this.updateFileColumnHeaders().then(() => {
                this.portsAndTerminalsService.uploadVesselPortCalls({
                    file: this.vesselPortCallsFiles[0],
                    portSKey: this.portId.toString()
                })
                    .then(() => {
                        this.notificationClientService.show(``, `File uploaded for processing`, NotificationType.Success);
                        if (this.onSave) {
                            this.onSave();
                        }
                        resolve();
                    }).catch(e => {
                        this.notificationClientService.show(`Error`, `File upload failed`, NotificationType.Error);
                        reject(e);
                    });

            }).catch(e => {
                this.notificationClientService.show(`Error`, `File upload failed`, NotificationType.Error);
                reject(e);
            });
        });
    }

    private validateFiles() {
        const requiredProperties = this.properties.filter(x => x.isRequired).map(x => x.property);
        if (requiredProperties && requiredProperties.length > 0) {
            const missingRequiredProperties = requiredProperties.filter(x => !this.mappings || this.mappings.filter(m => m.mappedToProperty == x).length == 0);

            if (missingRequiredProperties.length > 0) {
                return { valid: false, validationMessage: `${missingRequiredProperties.join(", ")} ${missingRequiredProperties.length == 1 ? "is" : "are"} required` };
            }
        }

        return { valid: true, validationMessage: null };
    }

    vesselPortCallsFilesChanged() {
        if (!this.vesselPortCallsFiles || this.vesselPortCallsFiles.length == 0)
            return;

        this.mappings = [];

        const uploadedFile = this.vesselPortCallsFiles && this.vesselPortCallsFiles.length > 0 ? this.vesselPortCallsFiles[0] : null;

        const fileExtension = uploadedFile && uploadedFile.name ? FileHelper.getFileExtension(uploadedFile.name) : null;

        if (!uploadedFile || !this._supportedTypes.some(x => x.mime === uploadedFile.type && (!x.extension || !fileExtension || (x.extension == fileExtension)))) {
            this.notificationClientService.show("Error", "Unsupported file type", NotificationType.Error);
            this.vesselPortCallsFiles.splice(0, this.vesselPortCallsFiles.length);
            this.isMappingConfig = false;
            return;
        }

        const reader = new FileReader();
        reader.onload = (e: any) => {
            import('xlsx').then(XLSX => {
                const data = e.target.result as string;
                const workbook = XLSX.read(data, { type: "binary" });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const range = XLSX.utils.decode_range(sheet["!ref"]);

                for (let i = range.s.c; i <= range.e.c; i++) {
                    const header = sheet[XLSX.utils.encode_cell({ c: i, r: 0 })];
                    if (!header) continue;

                    const excelColumn = header.h ? header.h : <string>header.v;

                    //Only headers that are string (in some CSV files headers with integer values were found.)
                    if (header.t == "s") {
                        const mapping: PropertyMappingModel = {
                            excelColumn: excelColumn,
                            columnNumber: i,
                            includeForImport: true,
                            mappedToProperty: null
                        }

                        const matchingProperty = this.getMatchingProperty(excelColumn);

                        if (matchingProperty.length > 0) {
                            mapping.mappedToProperty = matchingProperty[0].property;
                        }

                        this.mappings.push(mapping);
                    }
                }
            });
        };

        reader.readAsBinaryString(this.vesselPortCallsFiles[0]);
        this.isMappingConfig = true;
    }

    updateFileColumnHeaders() {
        return new Promise<void>((resolve, reject) => {
            try {
                const uploadedFile = this.vesselPortCallsFiles && this.vesselPortCallsFiles.length > 0 ? this.vesselPortCallsFiles[0] : null;
                const fileExtension = uploadedFile && uploadedFile.name ? FileHelper.getFileExtension(uploadedFile.name) : null;

                const reader = new FileReader();
                reader.onload = (e: any) => {
                    import('xlsx').then(XLSX => {
                        const data = e.target.result as string;
                        const workbook = XLSX.read(data, { type: "binary" });
                        const sheet = workbook.Sheets[workbook.SheetNames[0]];

                        this.mappings.filter(x => x.includeForImport).forEach(mapping => {
                            //update header value
                            XLSX.utils.sheet_add_aoa(sheet, [[mapping.mappedToProperty]], { origin: XLSX.utils.encode_cell({ c: mapping.columnNumber, r: 0 }) });
                        });

                        const base64 = XLSX.write(workbook, { bookType: 'csv', type: 'base64' });
                        const file = new File([FileHelper.toBlob(base64)], this.vesselPortCallsFiles[0].name.replace(fileExtension, 'csv'), { type: "text/csv" });
                        this.vesselPortCallsFiles[0] = file;
                        resolve();
                    });
                };
                reader.onerror = () => { reject("failed to load file"); }

                reader.readAsBinaryString(this.vesselPortCallsFiles[0]);
            }
            catch (e) {
                reject(e);
            }
        });
    }

    private getMatchingProperty(excelColumn: string) {

        const entityProperties = this.properties;

        const normalize = (input: string): string => {
            return input.replace(/\s/g, '').toLowerCase();
        }

        const headerNormalized = normalize(excelColumn);
        let matchingProperty = entityProperties.filter(x => {
            const anyMatches = x.possibleNameMatches
                ? Enumerable.from(x.possibleNameMatches).any(y => this.getEditDistance(normalize(y), headerNormalized) < 1)
                : false;

            return anyMatches;
        });

        if (matchingProperty.length == 0) {
            matchingProperty = entityProperties.filter(x => this.getEditDistance(normalize(x.property), headerNormalized) <= 2);
        }
        return matchingProperty;
    }

    private getEditDistance(a, b) {
        if (a.length == 0) return b.length;
        if (b.length == 0) return a.length;

        const matrix = [];

        // increment along the first column of each row
        let i;
        for (i = 0; i <= b.length; i++) {
            matrix[i] = [i];
        }

        // increment each column in the first row
        let j;
        for (j = 0; j <= a.length; j++) {
            matrix[0][j] = j;
        }

        // Fill in the rest of the matrix
        for (i = 1; i <= b.length; i++) {
            for (j = 1; j <= a.length; j++) {
                if (b.charAt(i - 1) == a.charAt(j - 1)) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                    matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, // substitution
                        Math.min(matrix[i][j - 1] + 1, // insertion
                            matrix[i - 1][j] + 1)); // deletion
                }
            }
        }

        return matrix[b.length][a.length];
    }

    onIncludeForImportChanged(mapping: PropertyMappingModel): void {
        if (!mapping.includeForImport) {
            mapping.mappedToProperty = null;
        }
    }

    detached() {
        this.dispose();
    }

    dispose() {
        this.isMappingConfig = false;
        this.vesselPortCallsFiles.splice(0, this.vesselPortCallsFiles.length);
        this.mappings = [];
        if (this._subscriptions && this._subscriptions.length > 0) {
            this._subscriptions.forEach(x => x.dispose());
            this._subscriptions = [];
        }
    }
}

export interface EntityProperty {
    property: string;
    possibleNameMatches: string[];
    isRequired: boolean;
}


export interface PropertyMappingModel {
    excelColumn: string;
    columnNumber: number;
    includeForImport: boolean;
    mappedToProperty: string;
}